'use client';

import {
  Box,
  Button,
  IconButton,
  Stack,
  Switch,
  Typography,
} from '@mui/material';
import { Provider } from '@repo/api-gw-sdk';
import { useRef, useState } from 'react';

import { FormTextField } from '@/components/form/formTextField';
import type { FieldValidation } from '@/components/form/validation/fieldValidation';

import { Tag } from '../tag';

interface TagRow {
  id: string;
  key: string;
  value: string;
}

const areTagRowsValid = (rows: TagRow[]) =>
  rows.every(
    (row, index) =>
      row.key.trim() !== '' &&
      rows.findIndex((other) => other.key === row.key) === index
  );

// A stored record cannot hold a repeated key. An empty key can, and the step remounts with it.
export const arePersistedTagKeysValid = (
  tags: Record<string, string> | undefined,
  keepOriginalTags: boolean
) =>
  keepOriginalTags || Object.keys(tags ?? {}).every((key) => key.trim() !== '');

const rowsToRecord = (rows: TagRow[]): Record<string, string> => {
  const record: Record<string, string> = {};
  for (const row of rows) {
    record[row.key] = row.value;
  }
  return record;
};

const recordToRows = (tags: Record<string, string> | undefined): TagRow[] =>
  Object.entries(tags ?? {}).map(([key, value], index) => ({
    id: `tag-${index}-${key}`,
    key,
    value,
  }));

const keyValidation = (row: TagRow, rows: TagRow[]): FieldValidation => ({
  validateInput: (value) => {
    if (value.trim() === '') {
      return false;
    }
    return !rows.some((other) => other.id !== row.id && other.key === value);
  },
  errorMessage: (value) => {
    if (value.trim() === '') {
      return 'Key is required';
    }
    if (rows.some((other) => other.id !== row.id && other.key === value)) {
      return 'Key must be unique';
    }
    return '';
  },
});

const TagEntryFields = ({
  row,
  rows,
  onKeyChange,
  onValueChange,
  onRemove,
}: {
  row: TagRow;
  rows: TagRow[];
  onKeyChange: (key: string) => void;
  onValueChange: (value: string) => void;
  onRemove: () => void;
}) => (
  <Stack
    direction='row'
    sx={{
      gap: '24px',
      alignItems: 'end',
      width: '560px',
    }}
  >
    <FormTextField
      sx={{ flex: '1' }}
      size='small'
      title='Key'
      placeholder=''
      value={row.key}
      validation={keyValidation(row, rows)}
      onChange={(event) => onKeyChange(event.target.value)}
    />
    <FormTextField
      sx={{ flex: '1' }}
      size='small'
      title='Value'
      placeholder=''
      value={row.value}
      onChange={(event) => onValueChange(event.target.value)}
    />
    <IconButton onClick={onRemove} aria-label='Remove tag'>
      <i className='material-symbols-close-rounded' />
    </IconButton>
  </Stack>
);

export const TagsSection = ({
  initialTags,
  tags = {},
  keepOriginalTags,
  setKeepOriginalTags,
  onChange,
  onValidityChange,
  provider,
}: {
  tags: Record<string, string> | undefined;
  initialTags: Record<string, string>;
  keepOriginalTags: boolean;
  setKeepOriginalTags?: (value: boolean) => void;
  onChange: (tags: Record<string, string>) => void;
  onValidityChange?: (isValid: boolean) => void;
  provider?: string;
}) => {
  const serializedTags = JSON.stringify(tags ?? {});
  const [rows, setRows] = useState(() => recordToRows(tags));
  const [appliedTags, setAppliedTags] = useState(serializedTags);
  const nextRowId = useRef(0);
  // Rows keep repeated keys that `tags` cannot. The parent writes this editor's record
  // back into `tags`; that echo must not rebuild the rows.
  if (serializedTags !== appliedTags) {
    setAppliedTags(serializedTags);
    setRows(recordToRows(tags));
  }

  const reportValidity = (
    nextRows: TagRow[],
    nextKeepOriginalTags: boolean
  ) => {
    onValidityChange?.(nextKeepOriginalTags || areTagRowsValid(nextRows));
  };

  const emitRows = (nextRows: TagRow[]) => {
    const record = rowsToRecord(nextRows);
    setAppliedTags(JSON.stringify(record));
    setRows(nextRows);
    onChange(record);
    reportValidity(nextRows, keepOriginalTags);
  };

  const createRowId = () => {
    nextRowId.current += 1;
    return `tag-new-${nextRowId.current}`;
  };

  return (
    <Stack
      className='w-full px-[40px]'
      sx={{
        gap: '24px',
      }}
    >
      {setKeepOriginalTags && (
        <Box className='flex justify-between items-center'>
          <Typography>
            {`Keep original ${provider === Provider.Gcp ? 'labels' : 'tags'}`}
          </Typography>
          <Switch
            size='small'
            checked={keepOriginalTags}
            onChange={() => {
              const nextKeepOriginalTags = !keepOriginalTags;
              setKeepOriginalTags(nextKeepOriginalTags);
              reportValidity(rows, nextKeepOriginalTags);
            }}
          />
        </Box>
      )}
      {keepOriginalTags &&
        initialTags &&
        Object.keys(initialTags).length > 0 && (
          <Stack
            className='w-full'
            direction='row'
            sx={{
              gap: '8px',
              flexWrap: 'wrap',
            }}
          >
            {Object.entries(initialTags).map((entry) => (
              <Tag
                variant='secondary'
                key={entry[0]}
                content={entry.filter((x) => x).join('=')}
              />
            ))}
          </Stack>
        )}
      {!keepOriginalTags && (
        <Stack
          sx={{
            gap: '40px',
          }}
        >
          {rows.length > 0 && (
            <Stack
              sx={{
                gap: '24px',
              }}
            >
              {rows.map((row) => (
                <TagEntryFields
                  key={row.id}
                  row={row}
                  rows={rows}
                  onKeyChange={(key) =>
                    emitRows(
                      rows.map((current) =>
                        current.id === row.id ? { ...current, key } : current
                      )
                    )
                  }
                  onValueChange={(value) =>
                    emitRows(
                      rows.map((current) =>
                        current.id === row.id ? { ...current, value } : current
                      )
                    )
                  }
                  onRemove={() =>
                    emitRows(rows.filter((current) => current.id !== row.id))
                  }
                />
              ))}
            </Stack>
          )}
          <Button
            sx={{ width: 'max-content' }}
            variant='outlined'
            onClick={() =>
              emitRows([...rows, { id: createRowId(), key: '', value: '' }])
            }
          >
            {`+ Add ${provider === Provider.Gcp ? 'Label' : 'Tag'}`}
          </Button>
        </Stack>
      )}
    </Stack>
  );
};
